import React, { useState, useRef } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import { Progress } from "reactstrap";
import "assets/css/listing-page.css";
import {
  Card,
  Row,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Spinner,
} from "reactstrap";
import helpers from "utils/helpers";
import constants from "constants";
import { useTranslation } from "context/TranslationProvider";

function PopulateRoutes({ items, isItemsLoading }) {
  const { t } = useTranslation();
  const [filterType, setFilterType] = useState(
    helpers.LISTING_TABLE_FILTERS.SENDER
  );
  const avatarCacheRef = useRef({});

  const filterListingByType = (item) => {
    switch (filterType) {
      case helpers.LISTING_TABLE_FILTERS.SENDER:
        return item.type === helpers.LISTING_TABLE_FILTERS.SENDER.toLowerCase();
      case helpers.LISTING_TABLE_FILTERS.TRAVELER:
        return item.type === helpers.LISTING_TABLE_FILTERS.TRAVELER.toLowerCase();
      default:
        return true;
    }
  };

  const filteredData = items.filter(filterListingByType);

  const getCachedAvatarUrl = (user) => {
    if (!user?._id) return undefined;

    if (!avatarCacheRef.current[user._id]) {
      if (user.profilePictureUrl) {
        avatarCacheRef.current[user._id] = `${constants.s3Bucket}/profile-pics/${user.profilePictureUrl}`;
      } else {
        const firstInitial = user.firstName ? user.firstName[0].toUpperCase() : "";
        const lastInitial = user.lastName ? user.lastName[0].toUpperCase() : "";
        avatarCacheRef.current[user._id] = `https://ui-avatars.com/api/?name=${firstInitial}+${lastInitial}&background=808080&color=fff&size=128`;
      }
    }
    return avatarCacheRef.current[user._id] || undefined;
  };

  const renderLoading = () => (
    <div className="center-loader">
      <div className="center-loader-content">
        <Spinner/>
        <p className="mt-3 text-muted">
          {t("common.loading", "Loading....")}
        </p>
      </div>
    </div>
  );

  const getFilterLabel = () => {
    if (filterType === helpers.LISTING_TABLE_FILTERS.SENDER) {
      return t("analytics.dashboard.mostPopular.sender", "Sender Listing");
    }
    if (filterType === helpers.LISTING_TABLE_FILTERS.TRAVELER) {
      return t("analytics.dashboard.mostPopular.traveler", "Traveler Listing");
    }
    return filterType;
  };

  return (
    <div>
      <Row>
        <div className="col">
          <Card className="shadow">
            {isItemsLoading ? (
              renderLoading()
            ) : (
              <ToolkitProvider
                data={filteredData}
                keyField="_id"
                columns={[
                  {
                    dataField: "fromPlace",
                    text: t("table.headers.from", "From"),
                    headerAlign: "left",
                    formatter: (cell) => cell?.name || "N/A",
                  },
                  {
                    dataField: "toPlace",
                    text: t("table.headers.to", "To"),
                    headerAlign: "left",
                    formatter: (cell) => cell?.name || "N/A",
                  },
                  {
                    dataField: "totalOrders",
                    text: t("analytics.dashboard.totalOrders", "Total Orders"),
                    headerAlign: "left",
                    sort: true,
                  },
                  {
                    dataField: "deliveryCount",
                    text: t("table.headers.deliveryCount", "Delivery Count"),
                    headerAlign: "left",
                    sort: true,
                  },
                  {
                    text: t("table.headers.users", "Users"),
                    headerAlign: "left",
                    align: "left",
                    formatter: (_, row) => (
                      <div className="d-flex">
                        {row.users?.map((user, index) => (
                          <img
                            key={user._id || index}
                            src={getCachedAvatarUrl(user)}
                            alt={user.firstName}
                            className="user-avatar"
                          />
                        ))}
                      </div>
                    ),
                  },
                  {
                    text: t("table.headers.completion", "Completion"),
                    headerAlign: "left",
                    align: "left",
                    formatter: (_, row) => {
                      const percent = row?.completion ?? 0;
                      return (
                        <div className="d-flex align-items-center">
                          <span className="completion mr-2">{percent}%</span>
                          <div className="flex-grow-1">
                            <Progress
                              max="100"
                              value={percent}
                              color={percent === 100 ? "success" : "primary"}
                            />
                          </div>
                        </div>
                      );
                    },
                  },
                ]}
              >
                {(props) => (
                  <div className="py-4">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                      
                      <div className="mx-4 d-flex align-items-center mb-2 mb-md-0">
                        <h3 className="h3 mb-0 mt--3">
                          {t("analytics.dashboard.mostPopular.title", "Most Populate Routes")}
                        </h3>
                      </div>

                      <div className="px-4 pb-1 mb-3 d-flex justify-content-start justify-content-md-end">
                        <div className="listing-header-right d-flex flex-column flex-sm-row align-items-start align-items-sm-center">
                          
                          <span className="text-sm font-weight-600 text-muted mr-sm-2 mb-1 mb-sm-0">
                            {t("analytics.dashboard.mostPopular.filter", "Populate Routes Filter")}
                          </span>
                          <UncontrolledDropdown>
                            <DropdownToggle caret className="btn-neutral">
                              {getFilterLabel()}
                            </DropdownToggle>
                            <DropdownMenu right>
                              <DropdownItem
                                onClick={() =>
                                  setFilterType(helpers.LISTING_TABLE_FILTERS.SENDER)
                                }
                              >
                                {t("analytics.dashboard.mostPopular.sender", "Sender Listing")}
                              </DropdownItem>
                              <DropdownItem
                                onClick={() =>
                                  setFilterType(helpers.LISTING_TABLE_FILTERS.TRAVELER)
                                }
                              >
                                {t("analytics.dashboard.mostPopular.traveler", "Traveler Listing")}
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </div>
                      </div>
                    </div>

                    <div className="table-responsive">
                      <BootstrapTable
                        {...props.baseProps}
                        bootstrap4
                        bordered={false}
                        noDataIndication={() => (
                          <div className="text-center py-6 text-muted">
                            {t("analytics.dashboard.notFound", "No listings found")}
                          </div>
                        )}
                      />
                    </div>
                  </div>
                )}
              </ToolkitProvider>
            )}
          </Card>
        </div>
      </Row>
    </div>
  );
}

export default PopulateRoutes;
